import { Text } from "@clipboard-health/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import ThumbDownAlt from "@mui/icons-material/ThumbDownAlt";
import ThumbDownAltOutlined from "@mui/icons-material/ThumbDownAltOutlined";
import ThumbUpAlt from "@mui/icons-material/ThumbUpAlt";
import ThumbUpAltOutlined from "@mui/icons-material/ThumbUpAltOutlined";
import { Button, Card, CardContent, CardHeader, IconButton, Stack, TextField } from "@mui/material";
import { type KeyboardEvent, type MouseEvent, type TouchEvent } from "react";

import { useShiftCardContext } from "./useShiftCard";
import { useWorkerRatingState } from "./useWorkerRatingState";

export const SHIFT_CARD_ACTION_GUARD_ATTRIBUTE = "data-shift-card-action-guard";

export function ShiftBoardWorkerRating() {
  const { shift } = useShiftCardContext();
  const { verified, worker, id, clockInOut } = shift;

  if (!verified && !clockInOut?.clockOut) {
    return null;
  }

  if (!isDefined(worker)) {
    return null;
  }

  const workerName = worker.name.split(" ")[0];
  return <ShiftBoardWorkerRatingContent shiftId={id} workerName={workerName} />;
}

interface ShiftBoardWorkerRatingContentProps {
  shiftId: string;
  workerName: string;
}

function ShiftBoardWorkerRatingContent(props: ShiftBoardWorkerRatingContentProps) {
  const { shiftId, workerName } = props;
  const {
    hasSubmitted,
    selectedRating,
    selectedReasons,
    availableReasons,
    reasonButtonColor,
    selectRating,
    toggleReason,
    updateCustomReason,
    submit,
    isSubmitDisabled,
    isCustomReasonSelected,
    customReason,
  } = useWorkerRatingState(shiftId);

  return (
    <Card
      variant="outlined"
      sx={{ backgroundColor: "#FAFAFA" }}
      {...{ [SHIFT_CARD_ACTION_GUARD_ATTRIBUTE]: true }}
      onClick={(event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
      }}
      onMouseDown={(event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
      }}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === " " || event.key === "Enter") {
          event.stopPropagation();
        }
      }}
      onTouchStart={(event: TouchEvent<HTMLDivElement>) => {
        event.stopPropagation();
      }}
    >
      {hasSubmitted ? undefined : (
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" gap={1}>
              <Text variant="h3">How was {workerName}&apos;s performance during this shift?</Text>
            </Stack>
          }
        />
      )}
      {hasSubmitted ? undefined : (
        <CardContent sx={{ paddingTop: 0 }}>
          <Stack spacing={3}>
            <Stack direction="row" justifyContent="center" spacing={4}>
              <RatingOption
                ariaLabel="Give a thumbs up"
                isSelected={selectedRating === "thumbsUp"}
                selectedIcon={<ThumbUpAlt fontSize="inherit" />}
                unselectedIcon={<ThumbUpAltOutlined fontSize="inherit" />}
                selectedColor="primary"
                onSelect={() => {
                  selectRating("thumbsUp");
                }}
              />
              <RatingOption
                ariaLabel="Give a thumbs down"
                isSelected={selectedRating === "thumbsDown"}
                selectedIcon={<ThumbDownAlt fontSize="inherit" />}
                unselectedIcon={<ThumbDownAltOutlined fontSize="inherit" />}
                selectedColor="error"
                onSelect={() => {
                  selectRating("thumbsDown");
                }}
              />
            </Stack>
            {availableReasons.length > 0 ? (
              <Stack direction="row" flexWrap="wrap" gap={1} justifyContent="center">
                {availableReasons.map((reason) => {
                  const isReasonSelected = selectedReasons.includes(reason);
                  const color = isReasonSelected ? reasonButtonColor : "inherit";

                  return (
                    <Button
                      key={reason}
                      variant={isReasonSelected ? "contained" : "outlined"}
                      color={color}
                      onClick={() => {
                        toggleReason(reason);
                      }}
                    >
                      {reason}
                    </Button>
                  );
                })}
              </Stack>
            ) : undefined}
            {isCustomReasonSelected ? (
              <TextField
                fullWidth
                multiline
                placeholder="Share more details"
                value={customReason}
                minRows={2}
                inputProps={{
                  onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => {
                    event.stopPropagation();
                    if (typeof event.nativeEvent.stopImmediatePropagation === "function") {
                      event.nativeEvent.stopImmediatePropagation();
                    }
                  },
                  onKeyUp: (event: KeyboardEvent<HTMLTextAreaElement>) => {
                    event.stopPropagation();
                    if (typeof event.nativeEvent.stopImmediatePropagation === "function") {
                      event.nativeEvent.stopImmediatePropagation();
                    }
                  },
                }}
                onChange={(event) => {
                  updateCustomReason(event.target.value);
                }}
              />
            ) : undefined}
            <Stack spacing={1} alignItems="center">
              <Button variant="contained" disabled={isSubmitDisabled} onClick={submit}>
                Submit
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      )}
      {hasSubmitted ? (
        <CardContent sx={{ paddingTop: 0 }}>
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 160 }}>
            <Text variant="h3" sx={{ color: "success.main", textAlign: "center" }}>
              Feedback submitted
            </Text>
          </Stack>
        </CardContent>
      ) : undefined}
    </Card>
  );
}

interface RatingOptionProps {
  ariaLabel: string;
  isSelected: boolean;
  onSelect: () => void;
  selectedIcon: React.ReactNode;
  unselectedIcon: React.ReactNode;
  selectedColor: "primary" | "error";
}

function RatingOption(props: RatingOptionProps) {
  const { ariaLabel, isSelected, onSelect, selectedIcon, unselectedIcon, selectedColor } = props;

  return (
    <Stack spacing={1} alignItems="center">
      <IconButton
        aria-pressed={isSelected}
        aria-label={ariaLabel}
        color={isSelected ? selectedColor : "default"}
        size="large"
        onClick={onSelect}
      >
        {isSelected ? selectedIcon : unselectedIcon}
      </IconButton>
    </Stack>
  );
}
